/**
 * CombatAI -- Sophisticated AI system for enemy and party auto-combat.
 *
 * Replaces basic enemy AI with behavior-driven target priority,
 * healing thresholds, buff timing, skill usage, and positioning.
 * Also provides party auto-AI for player characters when toggled on.
 *
 * @module CombatAI
 */

import eventBus from '../core/EventBus.js';
import debugLog from '../debug/DebugLog.js';

/** Healing thresholds */
const SUPPORT_HEAL_THRESHOLD = 0.4;  // Heal allies below 40% HP
const SELF_HEAL_THRESHOLD = 0.3;     // Self-heal below 30% HP
const PARTY_HEAL_THRESHOLD = 0.25;   // Party AI heals below 25%

/** Tile effect types considered hazardous */
const HAZARD_TILE_TYPES = ['fire', 'poison', 'trap'];

/** AP costs (mirrors CombatSystem) */
const AP_COSTS = {
  basic: 2,
  strong: 3,
  quick: 1,
  move: 1,
  item: 1,
  defend: 2,
};

class CombatAI {
  constructor() {
    /**
     * Auto-AI state per player character: Map<characterId, boolean>
     * @type {Map<string, boolean>}
     */
    this._autoAI = new Map();

    /**
     * Damage tracking for boss threat assessment: Map<combatantId, totalDamage>
     * @type {Map<string, number>}
     */
    this._threatTable = new Map();

    /**
     * Reference to the CombatSystem.
     * @type {object|null}
     */
    this._combatSystem = null;

    /**
     * Reference to the SkillSystem.
     * @type {object|null}
     */
    this._skillSystem = null;

    /**
     * Reference to the TileEffects system.
     * @type {object|null}
     */
    this._tileEffects = null;

    /**
     * Reference to the StatusEffects system.
     * @type {object|null}
     */
    this._statusEffects = null;
  }

  /**
   * Initialize with references to other systems.
   * @param {object} combatSystem
   * @param {object} skillSystem
   * @param {object} tileEffects
   * @param {object} statusEffects
   */
  init(combatSystem, skillSystem, tileEffects, statusEffects) {
    this._combatSystem = combatSystem;
    this._skillSystem = skillSystem;
    this._tileEffects = tileEffects;
    this._statusEffects = statusEffects;
    this._autoAI.clear();
    this._threatTable.clear();

    // Track damage for threat table
    this._onHit = (data) => {
      if (data.attacker && data.damage > 0) {
        const current = this._threatTable.get(data.attacker.id) || 0;
        this._threatTable.set(data.attacker.id, current + data.damage);
      }
    };
    eventBus.on('combat:hit', this._onHit);
  }

  /**
   * Clean up event listeners.
   */
  destroy() {
    if (this._onHit) {
      eventBus.off('combat:hit', this._onHit);
      this._onHit = null;
    }
    this._autoAI.clear();
    this._threatTable.clear();
  }

  // ── Auto-AI Toggle ─────────────────────────────────

  /**
   * Toggle auto-AI for a player character.
   * @param {string} characterId
   * @param {boolean} [enabled]
   * @returns {boolean} new state
   */
  toggleAutoAI(characterId, enabled) {
    const newState = enabled !== undefined ? enabled : !this._autoAI.get(characterId);
    this._autoAI.set(characterId, newState);
    eventBus.emit('combat:autoAIToggle', { characterId, enabled: newState });
    return newState;
  }

  /**
   * Check if auto-AI is enabled for a character.
   * @param {string} characterId
   * @returns {boolean}
   */
  isAutoAI(characterId) {
    return this._autoAI.get(characterId) || false;
  }

  // ── Enemy AI (Sophisticated) ───────────────────────

  /**
   * Execute sophisticated AI for an enemy combatant.
   * @param {object} enemy - The enemy combatant
   * @returns {object[]} Array of actions taken
   */
  runEnemyAI(enemy) {
    const actions = [];
    if (!enemy.alive || enemy.ap <= 0) return actions;

    const cs = this._combatSystem;
    if (!cs) return actions;

    const behavior = enemy.behavior || 'melee';
    let maxIterations = 10;

    while (enemy.ap > 0 && maxIterations-- > 0 && enemy.alive) {
      // Check if combat ended
      if (cs.state === 'victory' || cs.state === 'defeat') break;

      const action = this._decideEnemyAction(enemy, behavior);
      if (!action) break;

      const result = this._executeAction(enemy, action);
      if (result) {
        actions.push(result);
        eventBus.emit('combat:aiAction', {
          combatant: enemy,
          action: result,
        });
      } else {
        break;
      }
    }

    return actions;
  }

  /**
   * Decide the best action for an enemy based on behavior type.
   * @param {object} enemy
   * @param {string} behavior
   * @returns {object|null} action descriptor
   */
  _decideEnemyAction(enemy, behavior) {
    const cs = this._combatSystem;

    // Priority 1: Support behavior -- heal injured allies
    if (behavior === 'support') {
      const healAction = this._checkSupportHealing(enemy);
      if (healAction) return healAction;

      const buffAction = this._checkBuffOpportunity(enemy);
      if (buffAction) return buffAction;
    }

    // Priority 2: Self-heal when low HP (all behaviors)
    if (enemy.hp / enemy.maxHp < SELF_HEAL_THRESHOLD) {
      const selfHeal = this._checkSelfHeal(enemy);
      if (selfHeal) return selfHeal;
    }

    // Priority 3: Use skills intelligently
    const skillAction = this._evaluateSkills(enemy, behavior);
    if (skillAction) return skillAction;

    // Priority 4: Positioning
    const posAction = this._evaluatePositioning(enemy, behavior);
    if (posAction) return posAction;

    // Priority 5: Basic attack
    const attackAction = this._evaluateBasicAttack(enemy, behavior);
    if (attackAction) return attackAction;

    // Priority 6: Move toward target
    const moveAction = this._evaluateMovement(enemy, behavior);
    if (moveAction) return moveAction;

    return null;
  }

  /**
   * Check if a support enemy should heal an ally.
   * @param {object} enemy
   * @returns {object|null}
   */
  _checkSupportHealing(enemy) {
    const cs = this._combatSystem;
    const allies = cs.enemies.filter(e => e.alive && e.id !== enemy.id);
    const injuredAlly = allies.find(a => a.hp / a.maxHp < SUPPORT_HEAL_THRESHOLD);

    if (injuredAlly && enemy.enemyData?.abilities) {
      // Find a healing skill
      const healSkill = this._findSkillByEffect(enemy, 'heal');
      if (healSkill && this._canUseEnemySkill(enemy, healSkill)) {
        return {
          type: 'skill',
          skillId: healSkill.skillId,
          target: injuredAlly,
          reason: `healing ${injuredAlly.name}`,
        };
      }
    }

    return null;
  }

  /**
   * Check if enemy should self-heal.
   * @param {object} enemy
   * @returns {object|null}
   */
  _checkSelfHeal(enemy) {
    if (enemy.enemyData?.abilities) {
      const healSkill = this._findSkillByEffect(enemy, 'heal');
      if (healSkill && this._canUseEnemySkill(enemy, healSkill)) {
        return {
          type: 'skill',
          skillId: healSkill.skillId,
          target: enemy,
          reason: 'self-healing',
        };
      }

      // Check for regenerate
      const regenSkill = this._findSkillByEffect(enemy, 'regenerate');
      if (regenSkill && this._canUseEnemySkill(enemy, regenSkill)) {
        return {
          type: 'skill',
          skillId: regenSkill.skillId,
          target: enemy,
          reason: 'regenerating',
        };
      }
    }
    return null;
  }

  /**
   * Check if a support enemy should buff allies.
   * @param {object} enemy
   * @returns {object|null}
   */
  _checkBuffOpportunity(enemy) {
    if (!enemy.enemyData?.abilities) return null;

    const cs = this._combatSystem;
    const allies = cs.enemies.filter(e => e.alive);

    // Find buff skills (war_cry, barkskin, fire_shield, etc.)
    for (const ability of enemy.enemyData.abilities) {
      const skillDef = this._skillSystem?.findSkill(ability.skillId);
      if (!skillDef) continue;

      const { skill } = skillDef;
      if (skill.targetType === 'self' || skill.targetType === 'aoe_ally') {
        // Check if buff is not already active
        if (skill.statusEffect) {
          const alreadyBuffed = enemy.statusEffects.some(e => e.id === skill.statusEffect);
          if (!alreadyBuffed && this._canUseEnemySkill(enemy, ability)) {
            const target = skill.targetType === 'self' ? enemy : allies[0];
            return {
              type: 'skill',
              skillId: ability.skillId,
              target: target || enemy,
              reason: `buffing with ${skill.name}`,
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Evaluate skill usage for an enemy.
   * @param {object} enemy
   * @param {string} behavior
   * @returns {object|null}
   */
  _evaluateSkills(enemy, behavior) {
    if (!enemy.enemyData?.abilities) return null;

    const cs = this._combatSystem;
    const targets = this._getOpponents(enemy);
    if (targets.length === 0) return null;

    // Score each available skill
    let bestSkill = null;
    let bestScore = -1;

    for (const ability of enemy.enemyData.abilities) {
      if (!this._canUseEnemySkill(enemy, ability)) continue;

      const skillDef = this._skillSystem?.findSkill(ability.skillId);
      if (!skillDef) continue;

      const { skill } = skillDef;
      const score = this._scoreSkill(enemy, skill, targets, behavior);

      if (score > bestScore) {
        bestScore = score;
        bestSkill = { ability, skill };
      }
    }

    if (bestSkill && bestScore > 0) {
      const target = this._selectSkillTarget(enemy, bestSkill.skill, targets, behavior);
      if (target) {
        return {
          type: 'skill',
          skillId: bestSkill.ability.skillId,
          target,
          reason: `using ${bestSkill.skill.name}`,
        };
      }
    }

    return null;
  }

  /**
   * Score a skill based on situation.
   * @param {object} enemy
   * @param {object} skill
   * @param {object[]} targets
   * @param {string} behavior
   * @returns {number}
   */
  _scoreSkill(enemy, skill, targets, behavior) {
    let score = 0;

    // AoE bonus: more targets = better
    if (skill.targetType === 'aoe_enemy') {
      const clumpedTargets = targets.filter(t => {
        const dist = Math.abs(t.gridX - enemy.gridX) + Math.abs(t.gridY - enemy.gridY);
        return dist <= 6;
      });
      if (clumpedTargets.length >= 2) {
        score += clumpedTargets.length * 3;
      } else {
        score += 1; // AoE on single target is inefficient
      }
    }

    // Debuff value: target high-threat enemies
    if (skill.statusEffect) {
      const statusDef = this._statusEffects?._combatSystem ? null : null; // basic check
      score += 2;
    }

    // Damage skills score based on multiplier
    if (skill.damageMultiplier) {
      const mult = Array.isArray(skill.damageMultiplier)
        ? skill.damageMultiplier[0]
        : skill.damageMultiplier;
      score += mult * 2;
    }

    // Priority from ability definition (lower number = higher priority)
    // Invert: priority 1 gets +5, priority 4 gets +2
    const abilityDef = enemy.enemyData?.abilities?.find(a => a.skillId === skill.id);
    if (abilityDef) {
      score += Math.max(0, 6 - abilityDef.priority);
    }

    // Boss behavior bonus for special skills
    if (behavior === 'boss') {
      score += 1;
    }

    return score;
  }

  /**
   * Select the best target for a skill.
   * @param {object} enemy
   * @param {object} skill
   * @param {object[]} targets
   * @param {string} behavior
   * @returns {object|null}
   */
  _selectSkillTarget(enemy, skill, targets, behavior) {
    if (skill.targetType === 'self') return enemy;
    if (skill.targetType === 'aoe_enemy' || skill.targetType === 'aoe_ally') {
      return targets[0] || null;
    }

    // For single-target skills, pick based on behavior
    return this._selectTarget(enemy, targets, behavior);
  }

  /**
   * Evaluate whether to do a basic attack.
   * @param {object} enemy
   * @param {string} behavior
   * @returns {object|null}
   */
  _evaluateBasicAttack(enemy, behavior) {
    if (enemy.ap < AP_COSTS.basic) return null;

    const cs = this._combatSystem;
    const targets = this._getAttackableTargets(enemy);
    if (targets.length === 0) return null;

    const target = this._selectTarget(enemy, targets, behavior);
    if (!target) return null;

    return {
      type: 'attack',
      attackType: 'basic',
      target,
      reason: `attacking ${target.name}`,
    };
  }

  /**
   * Evaluate movement based on behavior and positioning.
   * @param {object} enemy
   * @param {string} behavior
   * @returns {object|null}
   */
  _evaluateMovement(enemy, behavior) {
    if (enemy.ap < AP_COSTS.move) return null;

    const cs = this._combatSystem;
    const moveTiles = cs.getMovementRange(enemy);
    if (moveTiles.length === 0) return null;

    // Filter out hazardous tiles
    const safeTiles = moveTiles.filter(t => !this._isTileHazardous(t.x, t.y));
    const candidates = safeTiles.length > 0 ? safeTiles : moveTiles;

    const opponents = this._getOpponents(enemy);
    if (opponents.length === 0) return null;

    let bestTile = null;
    let bestScore = -Infinity;

    for (const tile of candidates) {
      const score = this._scoreMovePosition(enemy, tile, opponents, behavior);
      if (score > bestScore) {
        bestScore = score;
        bestTile = tile;
      }
    }

    // Only move if it improves position (score > 0)
    if (bestTile && bestScore > 0) {
      return {
        type: 'move',
        x: bestTile.x,
        y: bestTile.y,
        reason: 'repositioning',
      };
    }

    return null;
  }

  /**
   * Evaluate positioning: should the enemy move to avoid hazards or reposition?
   * @param {object} enemy
   * @param {string} behavior
   * @returns {object|null}
   */
  _evaluatePositioning(enemy, behavior) {
    if (enemy.ap < AP_COSTS.move) return null;

    // Check if currently on a hazardous tile
    if (this._isTileHazardous(enemy.gridX, enemy.gridY)) {
      const cs = this._combatSystem;
      const moveTiles = cs.getMovementRange(enemy);
      const safeTile = moveTiles.find(t => !this._isTileHazardous(t.x, t.y));
      if (safeTile) {
        return {
          type: 'move',
          x: safeTile.x,
          y: safeTile.y,
          reason: 'avoiding hazard',
        };
      }
    }

    // Ranged enemies: try to maintain distance
    if (behavior === 'ranged') {
      const opponents = this._getOpponents(enemy);
      const nearestDist = this._getNearestOpponentDistance(enemy, opponents);
      if (nearestDist <= 1 && enemy.ap >= AP_COSTS.move) {
        return this._evaluateMovement(enemy, behavior);
      }
    }

    return null;
  }

  /**
   * Score a movement position based on behavior.
   * @param {object} enemy
   * @param {object} tile - { x, y }
   * @param {object[]} opponents
   * @param {string} behavior
   * @returns {number}
   */
  _scoreMovePosition(enemy, tile, opponents, behavior) {
    let score = 0;
    const cs = this._combatSystem;

    // Distance to nearest opponent
    let minDist = Infinity;
    for (const opp of opponents) {
      const dist = Math.abs(tile.x - opp.gridX) + Math.abs(tile.y - opp.gridY);
      if (dist < minDist) minDist = dist;
    }

    const currentDist = this._getNearestOpponentDistance(enemy, opponents);

    switch (behavior) {
      case 'melee':
        // Move closer to enemies
        if (minDist < currentDist) score += (currentDist - minDist) * 2;
        // Bonus for being adjacent (can attack)
        if (minDist <= 1) score += 5;
        break;

      case 'ranged':
        // Maintain distance (ideal: 3-5 tiles)
        if (minDist >= 3 && minDist <= 5) score += 4;
        else if (minDist > 5) score += 1;
        else if (minDist <= 1) score -= 3; // Too close
        // Move away if too close
        if (minDist > currentDist && currentDist <= 2) score += 3;
        break;

      case 'tank':
        // Move toward enemies, try to block paths to squishier allies
        if (minDist < currentDist) score += (currentDist - minDist) * 2;
        if (minDist <= 1) score += 3;
        // Bonus for being between allies and opponents
        {
          const allies = cs.enemies.filter(e => e.alive && e.id !== enemy.id);
          for (const ally of allies) {
            if (ally.behavior === 'ranged' || ally.behavior === 'support') {
              const allyToOppDist = this._getNearestOpponentDistance(ally, opponents);
              const tileToOppDist = minDist;
              if (tileToOppDist < allyToOppDist) score += 2;
            }
          }
        }
        break;

      case 'support':
        // Stay near allies
        {
          const allies = cs.enemies.filter(e => e.alive && e.id !== enemy.id);
          let allyDist = 0;
          for (const ally of allies) {
            allyDist += Math.abs(tile.x - ally.gridX) + Math.abs(tile.y - ally.gridY);
          }
          if (allies.length > 0) {
            const avgAllyDist = allyDist / allies.length;
            if (avgAllyDist <= 2) score += 3;
            else if (avgAllyDist <= 4) score += 1;
          }
          // Stay back from opponents
          if (minDist >= 3) score += 2;
        }
        break;

      case 'boss':
        // Position centrally, close to opponents
        if (minDist < currentDist) score += (currentDist - minDist) * 1.5;
        // Central positioning bonus
        {
          const centerX = cs.cols / 2;
          const centerY = cs.rows / 2;
          const distToCenter = Math.abs(tile.x - centerX) + Math.abs(tile.y - centerY);
          if (distToCenter <= 3) score += 1;
        }
        break;
    }

    // Hazard penalty
    if (this._isTileHazardous(tile.x, tile.y)) {
      score -= 10;
    }

    return score;
  }

  // ── Target Selection ───────────────────────────────

  /**
   * Select best target based on behavior.
   * @param {object} unit
   * @param {object[]} targets
   * @param {string} behavior
   * @returns {object|null}
   */
  _selectTarget(unit, targets, behavior) {
    if (targets.length === 0) return null;
    if (targets.length === 1) return targets[0];

    switch (behavior) {
      case 'melee':
        // Target lowest HP in range, or closest
        return this._targetLowestHPOrClosest(unit, targets);

      case 'ranged':
        // Target lowest HP, prefer squishy (low defense)
        return this._targetSquishiest(unit, targets);

      case 'tank':
        // Target whoever is attacking allies (highest threat to allies)
        return this._targetHighestThreatToAllies(unit, targets);

      case 'support':
        // Target enemies (debuff), prefer highest threat
        return this._targetHighestThreat(unit, targets);

      case 'boss':
        // Target highest-threat player
        return this._targetHighestThreat(unit, targets);

      default:
        return this._targetLowestHPOrClosest(unit, targets);
    }
  }

  /**
   * Target lowest HP in range, or closest if tied.
   */
  _targetLowestHPOrClosest(unit, targets) {
    return targets.reduce((best, t) => {
      const bestDist = Math.abs(best.gridX - unit.gridX) + Math.abs(best.gridY - unit.gridY);
      const tDist = Math.abs(t.gridX - unit.gridX) + Math.abs(t.gridY - unit.gridY);

      // Prefer lower HP
      if (t.hp < best.hp) return t;
      if (t.hp === best.hp && tDist < bestDist) return t;
      return best;
    });
  }

  /**
   * Target lowest defense (squishiest), then lowest HP.
   */
  _targetSquishiest(unit, targets) {
    return targets.reduce((best, t) => {
      if (t.defense < best.defense) return t;
      if (t.defense === best.defense && t.hp < best.hp) return t;
      return best;
    });
  }

  /**
   * Target whoever has dealt most damage (highest threat).
   */
  _targetHighestThreat(unit, targets) {
    return targets.reduce((best, t) => {
      const bestThreat = this._threatTable.get(best.id) || 0;
      const tThreat = this._threatTable.get(t.id) || 0;
      if (tThreat > bestThreat) return t;
      if (tThreat === bestThreat && t.hp < best.hp) return t;
      return best;
    });
  }

  /**
   * Target whoever is attacking allied units (for tank behavior).
   */
  _targetHighestThreatToAllies(unit, targets) {
    // Prioritize targets that are close to squishy allies
    const cs = this._combatSystem;
    const allies = cs.enemies.filter(e => e.alive && e.id !== unit.id);

    return targets.reduce((best, t) => {
      let bestScore = 0;
      let tScore = 0;

      for (const ally of allies) {
        const bestDist = Math.abs(best.gridX - ally.gridX) + Math.abs(best.gridY - ally.gridY);
        const tDist = Math.abs(t.gridX - ally.gridX) + Math.abs(t.gridY - ally.gridY);
        if (bestDist <= 2) bestScore += 2;
        if (tDist <= 2) tScore += 2;
      }

      // Add threat from threat table
      bestScore += this._threatTable.get(best.id) || 0;
      tScore += this._threatTable.get(t.id) || 0;

      if (tScore > bestScore) return t;
      return best;
    });
  }

  // ── Party Auto-AI ──────────────────────────────────

  /**
   * Execute auto-AI for a player character.
   * @param {object} player - Player combatant
   * @returns {object[]} Array of actions taken
   */
  runPartyAI(player) {
    const actions = [];
    if (!player.alive || player.ap <= 0) return actions;

    const cs = this._combatSystem;
    if (!cs) return actions;

    // Determine role from character class/behavior
    const role = this._determinePartyRole(player);
    let maxIterations = 10;

    while (player.ap > 0 && maxIterations-- > 0 && player.alive) {
      if (cs.state === 'victory' || cs.state === 'defeat') break;

      const action = this._decidePartyAction(player, role);
      if (!action) break;

      const result = this._executeAction(player, action);
      if (result) {
        actions.push(result);
        eventBus.emit('combat:aiAction', {
          combatant: player,
          action: result,
        });
      } else {
        break;
      }
    }

    return actions;
  }

  /**
   * Determine a player character's combat role.
   * @param {object} player
   * @returns {string} 'healer' | 'tank' | 'dps' | 'ranged'
   */
  _determinePartyRole(player) {
    const char = player.character;
    if (!char) return 'dps';

    // Check skill trees for healing abilities
    const treesToCheck = char.skillTrees || [];
    for (const treeId of treesToCheck) {
      if (treeId.includes('heal') || treeId.includes('holy') || treeId.includes('nature') || treeId.includes('restoration')) {
        return 'healer';
      }
      if (treeId.includes('guard') || treeId.includes('shield') || treeId.includes('defense') || treeId.includes('protection')) {
        return 'tank';
      }
    }

    // Check attributes
    if (char.attributes.vit >= 14 && char.attributes.str >= 12) return 'tank';
    if (char.attributes.wis >= 14 || char.attributes.int >= 14) return 'healer';

    return 'dps';
  }

  /**
   * Decide action for a party member in auto-AI.
   * @param {object} player
   * @param {string} role
   * @returns {object|null}
   */
  _decidePartyAction(player, role) {
    const cs = this._combatSystem;

    // Priority 1: Use healing item if HP critical (<25%)
    if (player.hp / player.maxHp < PARTY_HEAL_THRESHOLD) {
      if (player.ap >= AP_COSTS.item) {
        return {
          type: 'item',
          target: player,
          reason: 'critical HP, using potion',
        };
      }
    }

    // Priority 2: Healer role -- heal injured allies
    if (role === 'healer') {
      const allies = cs.players.filter(p => p.alive && p.hp / p.maxHp < PARTY_HEAL_THRESHOLD);
      if (allies.length > 0) {
        // Find healing skill
        const healSkill = this._findPlayerHealSkill(player);
        if (healSkill) {
          const target = allies.reduce((a, b) => a.hp < b.hp ? a : b);
          return {
            type: 'skill',
            skillId: healSkill.id,
            target,
            reason: `healing ${target.name}`,
          };
        }
      }
    }

    // Priority 3: Use available skills
    const skillAction = this._evaluatePartySkills(player, role);
    if (skillAction) return skillAction;

    // Priority 4: Basic attack
    const enemies = this._getOpponents(player);
    if (enemies.length > 0 && player.ap >= AP_COSTS.basic) {
      const attackable = this._getAttackableTargets(player);
      if (attackable.length > 0) {
        // Focus fire: target lowest HP enemy
        const target = attackable.reduce((a, b) => a.hp < b.hp ? a : b);
        return {
          type: 'attack',
          attackType: 'basic',
          target,
          reason: `attacking ${target.name}`,
        };
      }
    }

    // Priority 5: Move toward enemies
    if (enemies.length > 0 && player.ap >= AP_COSTS.move) {
      const moveTiles = cs.getMovementRange(player);
      if (moveTiles.length > 0) {
        // Move toward nearest enemy
        const nearestEnemy = enemies.reduce((a, b) => {
          const da = Math.abs(a.gridX - player.gridX) + Math.abs(a.gridY - player.gridY);
          const db = Math.abs(b.gridX - player.gridX) + Math.abs(b.gridY - player.gridY);
          return da < db ? a : b;
        });

        const safeTiles = moveTiles.filter(t => !this._isTileHazardous(t.x, t.y));
        const candidates = safeTiles.length > 0 ? safeTiles : moveTiles;

        const bestTile = candidates.reduce((best, t) => {
          const bestDist = Math.abs(best.x - nearestEnemy.gridX) + Math.abs(best.y - nearestEnemy.gridY);
          const tDist = Math.abs(t.x - nearestEnemy.gridX) + Math.abs(t.y - nearestEnemy.gridY);
          return tDist < bestDist ? t : best;
        });

        return {
          type: 'move',
          x: bestTile.x,
          y: bestTile.y,
          reason: 'moving toward enemy',
        };
      }
    }

    // Priority 6: Defend if no other action possible
    if (role === 'tank' && player.ap >= AP_COSTS.defend) {
      return {
        type: 'defend',
        reason: 'defending',
      };
    }

    return null;
  }

  /**
   * Evaluate skill usage for a party member.
   * @param {object} player
   * @param {string} role
   * @returns {object|null}
   */
  _evaluatePartySkills(player, role) {
    if (!this._skillSystem || !player.character) return null;

    const availableSkills = this._skillSystem.getAvailableSkills(player);
    if (availableSkills.length === 0) return null;

    for (const { skill, level } of availableSkills) {
      const check = this._skillSystem.canUseSkill(player, skill.id);
      if (!check.canUse) continue;

      // For damage skills, check targets
      if (skill.targetType === 'single_enemy' || skill.targetType === 'aoe_enemy') {
        const targets = this._getOpponents(player);
        if (targets.length > 0) {
          const target = targets.reduce((a, b) => a.hp < b.hp ? a : b);
          return {
            type: 'skill',
            skillId: skill.id,
            target,
            reason: `using ${skill.name}`,
          };
        }
      }

      // For self-buff skills
      if (skill.targetType === 'self') {
        return {
          type: 'skill',
          skillId: skill.id,
          target: player,
          reason: `buffing with ${skill.name}`,
        };
      }
    }

    return null;
  }

  /**
   * Find a healing skill on a player character.
   * @param {object} player
   * @returns {object|null}
   */
  _findPlayerHealSkill(player) {
    if (!this._skillSystem || !player.character) return null;

    const availableSkills = this._skillSystem.getAvailableSkills(player);
    for (const { skill } of availableSkills) {
      const check = this._skillSystem.canUseSkill(player, skill.id);
      if (!check.canUse) continue;

      if (skill.id.includes('heal') || skill.name?.toLowerCase().includes('heal')) {
        return skill;
      }
    }
    return null;
  }

  // ── Action Execution ───────────────────────────────

  /**
   * Execute an AI-decided action.
   * @param {object} unit
   * @param {object} action
   * @returns {object|null} result
   */
  _executeAction(unit, action) {
    const cs = this._combatSystem;
    if (!cs) return null;

    switch (action.type) {
      case 'attack': {
        const result = cs.attack(unit, action.target, action.attackType || 'basic');
        if (result.success) {
          eventBus.emit('combat:aiTargetSelected', {
            combatant: unit,
            target: action.target,
            reason: action.reason,
          });
        }
        return { type: 'attack', result, target: action.target, reason: action.reason };
      }

      case 'move': {
        // Walk path step by step (moveUnit only accepts adjacent moves)
        const path = cs.findPath(unit, action.x, action.y);
        if (path.length === 0) return null;

        let moved = false;
        for (const step of path) {
          const result = cs.moveUnit(unit, step.x, step.y);
          if (!result.success) break;
          moved = true;
        }
        return moved
          ? { type: 'move', x: unit.gridX, y: unit.gridY, reason: action.reason }
          : null;
      }

      case 'skill': {
        if (this._skillSystem) {
          const target = action.target;
          const targetInfo = target
            ? { x: target.gridX, y: target.gridY, combatant: target }
            : { x: unit.gridX, y: unit.gridY, combatant: unit };

          // For enemies, simulate skill usage (they don't have learned skills in normal sense)
          if (unit.type === 'enemy') {
            return this._executeEnemySkill(unit, action.skillId, targetInfo, action.reason);
          }

          // For players with auto-AI
          const result = this._skillSystem.useSkill(unit, action.skillId, targetInfo);
          return result.success
            ? { type: 'skill', skillId: action.skillId, target, reason: action.reason, result }
            : null;
        }
        return null;
      }

      case 'item': {
        const item = { id: 'health_potion', name: 'Health Potion', type: 'healing', healAmount: 20 };
        const result = cs.useItem(unit, item, action.target);
        return result.success
          ? { type: 'item', target: action.target, reason: action.reason }
          : null;
      }

      case 'defend': {
        if (unit.ap >= AP_COSTS.defend) {
          unit.ap -= AP_COSTS.defend;
          unit.statusEffects.push({
            id: 'defending',
            name: 'Defending',
            duration: 1,
            effects: [],
          });
          cs._addLog(`${unit.name} takes a defensive stance.`);
          return { type: 'defend', reason: action.reason };
        }
        return null;
      }

      default:
        return null;
    }
  }

  /**
   * Execute a skill for an enemy combatant.
   * Enemies use skills from their ability list without needing character.skills.
   * @param {object} enemy
   * @param {string} skillId
   * @param {object} targetInfo
   * @param {string} reason
   * @returns {object|null}
   */
  _executeEnemySkill(enemy, skillId, targetInfo, reason) {
    const skillDef = this._skillSystem?.findSkill(skillId);
    if (!skillDef) return null;

    const { skill } = skillDef;

    // Check AP
    if (enemy.ap < (skill.apCost || 2)) return null;

    // Check mana
    const manaCost = Array.isArray(skill.manaCost)
      ? skill.manaCost[0]
      : (skill.manaCost || 0);
    if (enemy.mana < manaCost) return null;

    // Deduct costs
    enemy.ap -= skill.apCost || 2;
    enemy.mana -= manaCost;

    // Set cooldown
    if (skill.cooldown > 0 && this._skillSystem) {
      this._skillSystem._setCooldown(enemy.id, skillId, skill.cooldown);
    }

    // Apply damage
    const cs = this._combatSystem;
    const target = targetInfo.combatant;
    let totalDamage = 0;

    if (skill.damageMultiplier && target && target.type !== enemy.type) {
      const mult = Array.isArray(skill.damageMultiplier)
        ? skill.damageMultiplier[0]
        : skill.damageMultiplier;
      const baseDmg = Math.max(1, enemy.damage);
      totalDamage = Math.floor(baseDmg * mult);

      if (target.alive) {
        target.hp -= totalDamage;
        target.hp = Math.max(0, target.hp);
        if (target.hp <= 0) {
          target.alive = false;
          cs._addLog(`${target.name} has been defeated!`);
          eventBus.emit('combat:kill', { attacker: enemy, target });
        }
      }
    }

    // Apply healing
    if (skill.healMultiplier && target && target.type === enemy.type) {
      const healMult = Array.isArray(skill.healMultiplier)
        ? skill.healMultiplier[0]
        : skill.healMultiplier;
      const healAmount = Math.floor(enemy.damage * healMult);
      target.hp = Math.min(target.maxHp, target.hp + healAmount);
      cs._addLog(`${enemy.name} heals ${target.name} for ${healAmount} HP with ${skill.name}.`);
    }

    // Apply status effect
    if (skill.statusEffect && target && this._statusEffects) {
      this._statusEffects.applyStatus(target, skill.statusEffect, skill.statusDuration || 2, enemy);
    }

    // Log
    const targetName = target ? target.name : 'the area';
    const dmgText = totalDamage > 0 ? ` for ${totalDamage} damage` : '';
    cs._addLog(`${enemy.name} uses ${skill.name} on ${targetName}${dmgText}!`);

    eventBus.emit('combat:attack', {
      attacker: enemy,
      target,
      type: 'skill',
      hit: totalDamage > 0,
      damage: totalDamage,
      critical: false,
      resultType: totalDamage > 0 ? 'hit' : 'effect',
    });

    return {
      type: 'skill',
      skillId,
      target,
      reason,
      damage: totalDamage,
    };
  }

  // ── Utility Methods ────────────────────────────────

  /**
   * Get opponents of a unit (alive enemies of opposing team).
   * @param {object} unit
   * @returns {object[]}
   */
  _getOpponents(unit) {
    const cs = this._combatSystem;
    if (!cs) return [];

    if (unit.type === 'enemy') {
      return [...cs.players, ...cs.companionCombatants].filter(p => p.alive);
    }
    return cs.enemies.filter(e => e.alive);
  }

  /**
   * Get targets within attack range.
   * @param {object} unit
   * @returns {object[]}
   */
  _getAttackableTargets(unit) {
    const cs = this._combatSystem;
    if (!cs) return [];

    const maxRange = (unit.behavior === 'ranged') ? 6 : 2;
    const opponents = this._getOpponents(unit);

    return opponents.filter(opp => {
      const dist = Math.abs(opp.gridX - unit.gridX) + Math.abs(opp.gridY - unit.gridY);
      return dist <= maxRange;
    });
  }

  /**
   * Check if a tile has hazardous effects.
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  _isTileHazardous(x, y) {
    if (!this._tileEffects) return false;

    const effects = this._tileEffects.getEffectsAt(x, y);
    if (!effects || effects.length === 0) return false;

    return effects.some(e => HAZARD_TILE_TYPES.includes(e.type));
  }

  /**
   * Get distance to nearest opponent.
   * @param {object} unit
   * @param {object[]} opponents
   * @returns {number}
   */
  _getNearestOpponentDistance(unit, opponents) {
    let minDist = Infinity;
    for (const opp of opponents) {
      const dist = Math.abs(opp.gridX - unit.gridX) + Math.abs(opp.gridY - unit.gridY);
      if (dist < minDist) minDist = dist;
    }
    return minDist;
  }

  /**
   * Find a skill in an enemy's ability list by effect keyword.
   * @param {object} enemy
   * @param {string} keyword - 'heal', 'regenerate', 'buff'
   * @returns {object|null} ability from enemy.enemyData.abilities
   */
  _findSkillByEffect(enemy, keyword) {
    if (!enemy.enemyData?.abilities) return null;

    for (const ability of enemy.enemyData.abilities) {
      if (ability.skillId.includes(keyword)) {
        return ability;
      }
    }
    return null;
  }

  /**
   * Check if an enemy can use a specific ability (AP, mana, cooldown).
   * @param {object} enemy
   * @param {object} ability - { skillId, priority }
   * @returns {boolean}
   */
  _canUseEnemySkill(enemy, ability) {
    const skillDef = this._skillSystem?.findSkill(ability.skillId);
    if (!skillDef) return false;

    const { skill } = skillDef;

    // AP check
    if (enemy.ap < (skill.apCost || 2)) return false;

    // Mana check
    const manaCost = Array.isArray(skill.manaCost)
      ? skill.manaCost[0]
      : (skill.manaCost || 0);
    if (enemy.mana < manaCost) return false;

    // Cooldown check
    if (this._skillSystem) {
      const cd = this._skillSystem.getCooldown(enemy.id, ability.skillId);
      if (cd > 0) return false;
    }

    return true;
  }

  // ── Difficulty Scaling ─────────────────────────────

  /**
   * Calculate encounter difficulty rating.
   * @param {object[]} enemies - Enemy combatants
   * @param {number} partyLevel
   * @returns {{ cr: number, difficulty: string }}
   */
  static calculateEncounterDifficulty(enemies, partyLevel) {
    let totalCR = 0;
    for (const enemy of enemies) {
      let cr = enemy.level || 1;
      if (enemy.enemyData?.variant === 'elite') cr *= 1.5;
      if (enemy.enemyData?.variant === 'boss') cr *= 2.5;
      if (enemy.enemyData?.behavior === 'boss') cr *= 2;
      totalCR += cr;
    }

    const ratio = totalCR / Math.max(1, partyLevel);
    let difficulty;
    if (ratio < 0.5) difficulty = 'trivial';
    else if (ratio < 1.0) difficulty = 'easy';
    else if (ratio < 1.5) difficulty = 'normal';
    else if (ratio < 2.0) difficulty = 'hard';
    else if (ratio < 3.0) difficulty = 'deadly';
    else difficulty = 'impossible';

    return { cr: totalCR, difficulty };
  }

  /**
   * Scale enemy count based on party level vs region level.
   * @param {number} baseCount
   * @param {number} partyLevel
   * @param {number[]} regionLevelRange
   * @returns {number}
   */
  static scaleEnemyCount(baseCount, partyLevel, regionLevelRange) {
    const [minLevel, maxLevel] = regionLevelRange;
    const regionMid = (minLevel + maxLevel) / 2;

    if (partyLevel > maxLevel + 2) {
      // Overleveled: fewer enemies, less challenge
      return Math.max(1, baseCount - 1);
    }
    if (partyLevel > regionMid) {
      // Slightly over: add one enemy
      return baseCount + 1;
    }
    if (partyLevel < minLevel) {
      // Underleveled: don't add more, just harder
      return baseCount;
    }

    return baseCount;
  }

  /**
   * Apply difficulty modifiers to enemy stats.
   * @param {object} enemyCombatant
   * @param {number} partyLevel
   * @param {number[]} regionLevelRange
   */
  static applyDifficultyModifiers(enemyCombatant, partyLevel, regionLevelRange) {
    const [minLevel, maxLevel] = regionLevelRange;

    // Underleveled party: enemies get bonus stats
    if (partyLevel < minLevel) {
      const deficit = minLevel - partyLevel;
      const bonus = 1 + deficit * 0.1;
      enemyCombatant.hp = Math.floor(enemyCombatant.hp * bonus);
      enemyCombatant.maxHp = Math.floor(enemyCombatant.maxHp * bonus);
      enemyCombatant.damage = Math.floor(enemyCombatant.damage * bonus);
    }

    // Apply elite/boss variant modifiers
    const variant = enemyCombatant.enemyData?.variant;
    if (variant === 'elite') {
      enemyCombatant.hp = Math.floor(enemyCombatant.hp * 1.5);
      enemyCombatant.maxHp = Math.floor(enemyCombatant.maxHp * 1.5);
      enemyCombatant.damage = Math.floor(enemyCombatant.damage * 1.3);
    } else if (variant === 'boss') {
      enemyCombatant.hp = Math.floor(enemyCombatant.hp * 2);
      enemyCombatant.maxHp = Math.floor(enemyCombatant.maxHp * 2);
      enemyCombatant.damage = Math.floor(enemyCombatant.damage * 1.5);
    }
  }
}

// Singleton
const combatAI = new CombatAI();
export default combatAI;
export { CombatAI };
